import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// FREE SHIPPING PROGRESS BAR
(function () {
  const SELECTOR = '[data-free-shipping-progress]';
  const THRESHOLD = 11000;
  let timer;

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async function getCart() {
    const response = await fetch('/cart.js?timestamp=' + Date.now(), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Failed to load cart');
    }

    return await response.json();
  }

  function renderBar(bar, subtotal) {
    const threshold = parseInt(bar.dataset.threshold, 10) || THRESHOLD;
    const remaining = Math.max(threshold - subtotal, 0);
    const progress = Math.min((subtotal / threshold) * 100, 100);

    const messageEl = bar.querySelector('[data-fs-message]');
    const amountEl = bar.querySelector('[data-fs-amount]');
    const fillEl = bar.querySelector('[data-fs-fill]');

    if (fillEl) fillEl.style.width = progress + '%';

    if (remaining > 0) {
      bar.classList.remove('is-success');

      if (messageEl) {
        messageEl.textContent = 'Add ' + formatMoney(remaining) + ' more to get free shipping';
      }

      if (amountEl) {
        amountEl.textContent = Math.round(progress) + '%';
      }
    } else {
      bar.classList.add('is-success');

      if (messageEl) {
        messageEl.textContent = 'You unlocked free shipping';
      }

      if (amountEl) {
        amountEl.textContent = '100%';
      }
    }
  }

  async function updateFreeShippingBars() {
    const bars = document.querySelectorAll(SELECTOR);
    if (!bars.length) return;

    try {
      const cart = await getCart();

      const subtotal =
        typeof cart.items_subtotal_price === 'number'
          ? cart.items_subtotal_price
          : typeof cart.total_price === 'number'
            ? cart.total_price
            : 0;

      bars.forEach((bar) => renderBar(bar, subtotal));
    } catch (error) {
      console.error('Free shipping progress error:', error);
    }
  }

  function scheduleUpdate(delay = 500) {
    clearTimeout(timer);
    timer = setTimeout(updateFreeShippingBars, delay);
  }

  document.addEventListener('DOMContentLoaded', function () {
    scheduleUpdate(300);
  });

  document.addEventListener('shopify:section:load', function () {
    scheduleUpdate(300);
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('cart-drawer-component, cart-items-component, .quantity-selector, .cart-drawer')) {
      scheduleUpdate(700);
    }
  });

  const observer = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        scheduleUpdate(500);
        break;
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  window.updateFreeShippingBars = updateFreeShippingBars;
})();